import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Tooltip, Popup } from 'react-leaflet';
import { apiClient } from '../../api_actions/index';
import { Descriptions, Collapse } from 'antd';
import bytes from 'bytes';
import './styles.scss';

import { useIsSuperAdmin } from '../../store';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import LoadingOrbitalSpinner from '../../components/LoadingOrbitalSpinner/LoadingOrbitalSpinner';
const { Panel } = Collapse;


export default function MapPage({ isHalfScreen = false }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const isSuperAdmin = useIsSuperAdmin();

  const trailerIcon = L.icon({
    iconUrl: '/assets/trailer.png',
    iconSize: [38, 95],
    iconAnchor: [22, 94],
    popupAnchor: [-3, -76]
  });

const renderNestedInfo = (obj, title) => {
  if (!obj) return null;
  
  const renderValue = (key, value) => {
    // Handle arrays
    if (Array.isArray(value)) {
      if (value.length === 0) return 'Empty';
      
      // If it's a simple array of primitives
      if (value.every(item => typeof item !== 'object')) {
        return value.join(', ');
      }
      
      // For arrays of objects
      return <pre style={{ margin: 0 }}>{JSON.stringify(value, null, 2)}</pre>;
    }
    
    // Handle objects
    if (typeof value === 'object' && value !== null) {
      return <pre style={{ margin: 0 }}>{JSON.stringify(value, null, 2)}</pre>;
    }
    
    if (key === "start_time" && value !== null) {
      return <pre style={{ margin: 0 }}>{new Date(value * 1000).toISOString()}</pre>
    }

    if (key === "input_bytes" && value !== null) {
      return <pre style={{ margin: 0 }}>{bytes.format(value, {unitSeparator: ' '})}</pre>
    }
    
    if (key === "output_bytes" && value !== null) {
      return <pre style={{ margin: 0 }}>{bytes.format(value, {unitSeparator: ' '})}</pre>
    }

    // Handle primitives
    return String(value);
  };
  
  return (
    <Descriptions 
      title={title} 
      size="small" 
      column={1}
      bordered
      style={{ marginTop: '8px' }}
    >
      {Object.entries(obj).map(([key, value]) => (
        <Descriptions.Item key={key} label={key}>
          {renderValue(key, value)}
        </Descriptions.Item>
      ))}
    </Descriptions>
  );
};

  const loadData = async () => {
    try {
      setLoading(true);
      const result = await apiClient.post("read/deployments");
      setData(result?.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', padding: '32px' }}>
        <LoadingOrbitalSpinner />
        <div>Loading map data...</div>
      </div>
    );
  }

  return (
    <div className={`map-page-container ${isHalfScreen ? 'half-screen' : ''}`}>
      <MapContainer
        center={[38.62923931344675, -90.21695917041448]}
        zoom={9}
        style={{ width: '100%', height: '100%' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          maxZoom={19}
        />

        {data.map((deployment, index) => {
          
          
          

          let weather = deployment.weather_report?.weather?.[0]?.description || 'N/A';
          if (weather.length > 1) {
            weather = weather.charAt(0).toUpperCase() + weather.slice(1);
          }
          const periscope_info = Object.values(deployment.periscope_metadata ?? {})[0]
          const connectivity_info = deployment.connectivity_info ?? {};
          const router_info = deployment.router_info ?? {}
          const streams_info = deployment.stream_info?.[deployment.deployment_id] ?? {}
          const battery_info = deployment.battery_info ?? {}

          const lat = deployment.location?.latitude ?? router_info?.latitude;
          const long = deployment.location?.longitude ?? router_info?.longitude;

          if (!lat || !long) return null;

          return (
            <Marker 
              key={index} 
              position={[lat, long]} 
              icon={trailerIcon}
            >
              <Tooltip direction="right" permanent>
                <p><strong>{deployment.hardware_info?.tag}</strong></p>
                <p><strong>{deployment.location.name}</strong></p>
              </Tooltip>
              {isSuperAdmin && (
                <Popup maxWidth={400}
                  className="custom-popup"
                >
                <div style={{ 
                    background: 'white', 
                    padding: '10px', 
                    borderRadius: '5px', 
                    boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                    minWidth: '300px',
                    width: 'max-content',
                }}>
                  <h3 style={{ margin: '0 0 10px 0' }}>{deployment.location.name}</h3>
                  <p><strong>Health:</strong> {deployment.deployment_health?.name}</p>
                  
                  
                    <Collapse 
                      ghost 
                      size="small"
                      style={{ marginTop: '10px' }}
                    >
                      {connectivity_info && (
                        <Panel header="Compute Info" key="1">
                          {renderNestedInfo(connectivity_info)}
                        </Panel>
                      )}
                      
                      {router_info && (
                        <Panel header="Router Info" key="2">
                          {renderNestedInfo(router_info)}
                        </Panel>
                      )}
                      
                      {streams_info && (
                        <Panel header="Streams Info" key="3">
                          {renderNestedInfo(streams_info)}
                        </Panel>
                      )}

                      {periscope_info && (
                        <Panel header="Periscope Info" key="4">
                          {renderNestedInfo(periscope_info)}
                        </Panel>
                      )}

                      {battery_info && (
                        <Panel header="Battery Info" key="5">
                          {renderNestedInfo(battery_info)}
                        </Panel>
                      )

                      }
                    </Collapse>
                  
                  
                  <p><strong>Weather:</strong> {weather}</p>
                </div>
              </Popup>
              )}
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
}
